import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// Async thunk for creating an article
export const createArticle = createAsyncThunk(
  "article/create",
  async ({ keyword, intent, isDraft = false }, { rejectWithValue }) => {
    try {
      const response = await fetch("https://knovik.app.n8n.cloud/webhook/botwriter-webapp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "create",
          keyword,
          intent,
          isDraft: isDraft ? "true" : "false",
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get response text first
      const responseText = await response.text();
      
      // Debug: Log the raw response
      console.log("📄 Raw response:", responseText);
      
      // Check if response is empty
      if (!responseText || responseText.trim().length === 0) {
        throw new Error("Empty response from server");
      }

      // Try to parse JSON
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Failed to parse response as JSON:", parseError);
        console.error("Response text:", responseText);
        throw new Error(`Invalid JSON response from server. Response: ${responseText.substring(0, 200)}`);
      }

      // Check if result indicates success
      if (result && typeof result === 'object' && result.success === false) {
        throw new Error(result.message || "Request failed");
      }

      // Debug: Log the full response
      console.log("✅ Create article response:", result);

      return result;
    } catch (error) {
      console.error("❌ Create article error:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for refining an article
export const refineArticle = createAsyncThunk(
  "article/refine",
  async (
    { keyword, intent, currentContent, refinements, isDraft = false },
    { rejectWithValue }
  ) => {
    try {
      console.log("🔄 Refining article with:", { keyword, intent, currentContent: currentContent?.substring(0, 100) + "...", refinements });
      
      const response = await fetch("https://knovik.app.n8n.cloud/webhook/botwriter-webapp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "reply",
          keyword,
          intent,
          currentContent,
          refinements,
          isDraft: isDraft ? "true" : "false",
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get response text first
      const responseText = await response.text();
      
      // Check if response is empty
      if (!responseText || responseText.trim().length === 0) {
        throw new Error("Empty response from server");
      }

      // Try to parse JSON
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Failed to parse response as JSON:", parseError);
        console.error("Response text:", responseText);
        throw new Error(`Invalid JSON response from server. Response: ${responseText.substring(0, 200)}`);
      }

      // Check if result indicates success
      if (result && typeof result === 'object' && result.success === false) {
        throw new Error(result.message || "Request failed");
      }

      // Debug: Log the full response
      console.log("✅ Refine article response:", result);
      console.log("📄 Response structure:", JSON.stringify(result, null, 2));

      return result;
    } catch (error) {
      console.error("❌ Refine article error:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for regenerating an article
export const regenerateArticle = createAsyncThunk(
  "article/regenerate",
  async ({ keyword, intent }, { rejectWithValue }) => {
    try {
      const response = await fetch("https://knovik.app.n8n.cloud/webhook/botwriter-webapp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "create",
          keyword,
          intent,
          isDraft: "false",
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get response text first
      const responseText = await response.text();
      
      // Check if response is empty
      if (!responseText || responseText.trim().length === 0) {
        throw new Error("Empty response from server");
      }

      // Try to parse JSON
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Failed to parse response as JSON:", parseError);
        console.error("Response text:", responseText);
        throw new Error(`Invalid JSON response from server. Response: ${responseText.substring(0, 200)}`);
      }

      // Check if result indicates success
      if (result && typeof result === 'object' && result.success === false) {
        throw new Error(result.message || "Request failed");
      }

      return result;
    } catch (error) {
      console.error("❌ Regenerate article error:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for saving as draft
export const saveAsDraft = createAsyncThunk(
  "article/saveAsDraft",
  async (
    { keyword, intent, currentContent, author },
    { rejectWithValue }
  ) => {
    try {
      
      const response = await fetch("https://knovik.app.n8n.cloud/webhook/botwriter-webapp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "reply",
          keyword,
          intent,
          currentContent,
          isDraft: "true",
          author,
          slug: `draft-${Date.now()}`,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get response text first
      const responseText = await response.text();
      
      // Check if response is empty
      if (!responseText || responseText.trim().length === 0) {
        throw new Error("Empty response from server");
      }

      // Try to parse JSON
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Failed to parse response as JSON:", parseError);
        console.error("Response text:", responseText);
        throw new Error(`Invalid JSON response from server. Response: ${responseText.substring(0, 200)}`);
      }

      // Check if result indicates success
      if (result && typeof result === 'object' && result.success === false) {
        throw new Error(result.message || "Request failed");
      }

      console.log("✅ Draft saved response:", result);

      return {
        ...result,
        author, // Include author in the returned data
      };
    } catch (error) {
      console.error("❌ Save draft error:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching articles
export const fetchArticles = createAsyncThunk(
  "article/fetchArticles",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await fetch("https://knovik.app.n8n.cloud/webhook/botwriter-webapp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "get_articles",
          userId: userId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get response text first
      const responseText = await response.text();
      
      // Check if response is empty
      if (!responseText || responseText.trim().length === 0) {
        throw new Error("Empty response from server");
      }

      // Try to parse JSON
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Failed to parse response as JSON:", parseError);
        console.error("Response text:", responseText);
        throw new Error(`Invalid JSON response from server. Response: ${responseText.substring(0, 200)}`);
      }

      if (result.success && result.data) {
        return result.data.articles || result.data || [];
      } else {
        return [];
      }
    } catch (error) {
      console.error("❌ Fetch articles error:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Helper function to extract content from API response
const extractContentFromResponse = (response) => {
  console.log("🔍 Extracting content from:", response);
  
  // Handle case where response might be null or undefined
  if (!response || typeof response !== 'object') {
    console.log("❌ Invalid response object");
    return "";
  }
  
  // Try different possible paths for content
  const possiblePaths = [
    response?.content,
    response?.data?.content,
    response?.data?.message?.content,
    response?.message?.content,
    response?.data?.message,
    response?.message,
    response?.data?.response,
    response?.response,
    response?.text,
    response?.data?.text
  ];
  
  for (let i = 0; i < possiblePaths.length; i++) {
    const content = possiblePaths[i];
    if (content && typeof content === 'string' && content.trim().length > 0) {
      console.log(`✅ Found content at path ${i}:`, content.substring(0, 100) + "...");
      return content;
    }
  }
  
  console.log("❌ No content found in response");
  return "";
};

// Helper function to extract draft response message
const extractDraftResponse = (response) => {
  // Handle case where response might be null or undefined
  if (!response || typeof response !== 'object') {
    return "Draft saved successfully!";
  }

  const possiblePaths = [
    response?.message,
    response?.data?.message,
    response?.data?.response,
    response?.response,
    response?.status,
    response?.data?.status,
  ];
  
  for (let path of possiblePaths) {
    if (path && typeof path === 'string' && path.trim().length > 0) {
      return path;
    }
  }
  
  return "Draft saved successfully!";
};

// Initial state
const initialState = {
  // Current article being worked on
  currentArticle: {
    keyword: "",
    intent: "",
    content: "",
    isDraft: false,
  },

  // Articles list for dashboard
  articles: [],

  // UI state
  isLoading: false,
  error: null,

  // Operation tracking
  lastAction: null,
  lastUpdated: null,

  // Draft state
  draftSaved: false,
  draftResponse: null,

  // Form data
  formData: {
    keyword: "",
    intent: "",
  },

  // Refinements and author
  refinements: "",
  author: "",
};

// Article slice
const articleSlice = createSlice({
  name: "article",
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Set form data
    setFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },

    // Set refinements
    setRefinements: (state, action) => {
      state.refinements = action.payload;
    },

    // Clear refinements
    clearRefinements: (state) => {
      state.refinements = "";
    },

    // Set author
    setAuthor: (state, action) => {
      state.author = action.payload;
    },

    // Clear author
    clearAuthor: (state) => {
      state.author = "";
    },

    // Reset article state - ENHANCED TO RESET EVERYTHING
    resetArticle: (state) => {
      console.log("🔄 Resetting article state to initial state");
      
      // Reset current article
      state.currentArticle = {
        keyword: "",
        intent: "",
        content: "",
        isDraft: false,
      };
      
      // Reset form data
      state.formData = {
        keyword: "",
        intent: "",
      };
      
      // Reset all UI and operation states
      state.error = null;
      state.isLoading = false;
      state.draftSaved = false;
      state.draftResponse = null;
      state.lastAction = null;
      state.lastUpdated = null;
      state.refinements = "";
      state.author = "";
      
      console.log("✅ Article state completely reset");
    },

    // Set content directly (for manual updates)
    setContent: (state, action) => {
      state.currentArticle.content = action.payload;
      state.lastUpdated = new Date().toISOString();
      console.log("📝 Content set manually:", action.payload.substring(0, 100) + "...");
    },

    // Clear draft saved status
    clearDraftSaved: (state) => {
      state.draftSaved = false;
      state.draftResponse = null;
    },

    // Force UI update (for debugging)
    forceUpdate: (state) => {
      state.lastUpdated = new Date().toISOString();
    },
  },
  extraReducers: (builder) => {
    // Create Article
    builder
      .addCase(createArticle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastAction = "create";
        state.draftSaved = false;
        state.draftResponse = null;
      })
      .addCase(createArticle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        
        const content = extractContentFromResponse(action.payload);
        
        state.currentArticle = {
          keyword: state.formData.keyword,
          intent: state.formData.intent,
          content: content,
          isDraft: action.payload?.metadata?.isDraft || false,
        };
        
        state.lastUpdated = new Date().toISOString();
        state.draftSaved = false;
        state.draftResponse = null;
        
        console.log("✅ Create article - Content updated in state:", content.substring(0, 100) + "...");
      })
      .addCase(createArticle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to create article";
      });

    // Refine Article - REPLACE CONTENT WITH REFINED VERSION
    builder
      .addCase(refineArticle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastAction = "refine";
        state.draftSaved = false;
        state.draftResponse = null;
        console.log("🔄 Refine article - Starting refinement...");
      })
      .addCase(refineArticle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        
        console.log("🔄 Refine article - Received response:", action.payload);
        console.log("Previous content length:", state.currentArticle.content?.length || 0);
        
        const refinedContent = extractContentFromResponse(action.payload);
        
        if (refinedContent && refinedContent.trim().length > 0) {
          // REPLACE the current content with the refined content
          state.currentArticle.content = refinedContent;
          state.lastUpdated = new Date().toISOString();
          
          console.log("✅ Refine article - Content REPLACED with refined version");
          console.log("✅ New content length:", refinedContent.length);
          console.log("✅ New content preview:", refinedContent.substring(0, 200) + "...");
        } else {
          console.log("❌ Refine article - No refined content received, keeping original");
          console.log("❌ Response payload:", JSON.stringify(action.payload, null, 2));
        }
        
        // Clear refinements after successful refinement
        state.refinements = "";
        state.draftSaved = false;
        state.draftResponse = null;
      })
      .addCase(refineArticle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to refine article";
        console.log("❌ Refine article - Failed:", action.payload);
      });

    // Regenerate Article
    builder
      .addCase(regenerateArticle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastAction = "regenerate";
        state.draftSaved = false;
        state.draftResponse = null;
      })
      .addCase(regenerateArticle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        
        const content = extractContentFromResponse(action.payload);
        
        state.currentArticle.content = content;
        state.lastUpdated = new Date().toISOString();
        state.draftSaved = false;
        state.draftResponse = null;
      })
      .addCase(regenerateArticle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to regenerate article";
      });

    // Save as Draft - WITH AUTHOR AND RESPONSE DISPLAY
    builder
      .addCase(saveAsDraft.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastAction = "save_draft";
      })
      .addCase(saveAsDraft.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.currentArticle.isDraft = true;
        state.draftSaved = true;
        state.lastUpdated = new Date().toISOString();
        
        // Store the draft response to show to user
        const draftResponseData = extractDraftResponse(action.payload);
        state.draftResponse = {
          ...draftResponseData,
          author: action.payload?.author,
          timestamp: new Date().toISOString(),
          fullResponse: action.payload,
        };
        
        console.log("✅ Draft saved with response:", state.draftResponse);
      })
      .addCase(saveAsDraft.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to save draft";
        state.draftSaved = false;
        state.draftResponse = null;
      });

    // Fetch Articles
    builder
      .addCase(fetchArticles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchArticles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.articles = action.payload;
      })
      .addCase(fetchArticles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch articles";
        state.articles = [];
      });
  },
});

// Export actions
export const {
  clearError,
  setFormData,
  setRefinements,
  clearRefinements,
  setAuthor,
  clearAuthor,
  resetArticle,
  setContent,
  clearDraftSaved,
  forceUpdate,
} = articleSlice.actions;

// Export selectors
export const selectArticle = (state) => state.article.currentArticle;
export const selectArticles = (state) => state.article.articles;
export const selectIsLoading = (state) => state.article.isLoading;
export const selectError = (state) => state.article.error;
export const selectFormData = (state) => state.article.formData;
export const selectRefinements = (state) => state.article.refinements;
export const selectAuthor = (state) => state.article.author;
export const selectDraftSaved = (state) => state.article.draftSaved;
export const selectDraftResponse = (state) => state.article.draftResponse;
export const selectLastAction = (state) => state.article.lastAction;
export const selectLastUpdated = (state) => state.article.lastUpdated;

export default articleSlice.reducer;